<template>
    <v-container fluid class="py-6 px-6 api-key-page">
        <v-row>
            <v-col cols="12">
                <div class="home-like-header mb-4">
                    <template v-if="userApiKeyFeatureEnabled">
                        <h2 class="text-h5 mb-1">Your personal API key</h2>
                        <p class="mb-0 api-key-help-text">
                            This key is yours alone and works for the company you currently have selected. Keep it private &ndash; generating a new key immediately invalidates the previous one. If
                            you switch the company above, you will see the key for that company here.
                        </p>
                    </template>
                    <template v-else>
                        <h2 class="text-h5 mb-1">API key</h2>
                        <p class="mb-0 api-key-help-text">This company uses one shared API key for all integrations.</p>
                    </template>
                </div>
            </v-col>
        </v-row>

        <!-- Personal-API-Key: vorerst deaktiviert, siehe user_api_key_feature_enabled im Store -->
        <v-row v-if="userApiKeyFeatureEnabled" align="end">
            <v-col cols="12" md="6" lg="6">
                <v-card outlined class="pa-4 api-card">
                    <template v-if="apiKey">
                        <v-text-field :value="displayApiKey" label="Current API key" readonly filled dense hide-details prepend-inner-icon="mdi-key-variant" aria-live="polite">
                            <template v-slot:append>
                                <v-btn
                                    icon
                                    x-small
                                    @click.stop.prevent="toggleApiKey"
                                    :disabled="loading"
                                    :aria-label="showApiKey ? 'Hide API key' : 'Show API key'"
                                    :title="showApiKey ? 'Hide API key' : 'Show API key'">
                                    <v-icon small>{{ showApiKey ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                                </v-btn>

                                <v-btn icon x-small @click.stop.prevent="copyApiKey" :disabled="loading" aria-label="Copy API key" title="Copy API key">
                                    <v-icon small>mdi-content-copy</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>

                        <div class="mt-4 d-flex align-center flex-wrap gap-2">
                            <v-btn color="error" rounded @click="showRegenerateConfirm = true" :disabled="loading">Regenerate API key</v-btn>
                            <span class="api-key-help-text ml-3">
                                Use it in your AI assistant &rsaquo;
                                <a href="#" class="docs-link" @click.prevent="$emit('navigate', 6)">Set up the MCP server</a>
                            </span>
                        </div>
                    </template>

                    <template v-else-if="$store.state.personal_api_key === ''">
                        <p class="mb-3 api-key-help-text">You do not have a personal API key for this company yet.</p>
                        <v-btn color="primary" rounded @click="generateApiKey" :loading="loading" :disabled="loading">Create API key</v-btn>
                    </template>

                    <p v-else-if="loading" class="mb-0 api-key-help-text">Loading your API key&hellip;</p>

                    <template v-else>
                        <p class="mb-3 api-key-help-text">Could not load your API key. If you already have one, it still works.</p>
                        <v-btn color="primary" rounded outlined @click="fetchApiKey">Try again</v-btn>
                    </template>
                </v-card>
            </v-col>

            <v-col cols="12" md="6" lg="6" class="d-flex">
                <v-card outlined class="pa-4 docs-card">
                    <div class="font-weight-bold mb-2">Documentation</div>
                    <p class="mb-2 api-key-help-text">API docs and integration details.</p>
                    <a href="https://docs.quaro.io" target="_blank" rel="noopener noreferrer" class="docs-link">https://docs.quaro.io</a>

                    <p class="mb-0 mt-4 api-key-help-text">
                        Rather work inside an AI assistant than write code?
                        <a href="#" class="docs-link" @click.prevent="$emit('navigate', 6)">Connect Quaro via the MCP server</a>
                        and read your data straight from Claude, ChatGPT or Perplexity.
                    </p>
                </v-card>
            </v-col>
        </v-row>

        <v-row class="mt-1">
            <v-col cols="12">
                <v-card outlined class="pa-4 api-card">
                    <div class="font-weight-bold text-h6 mb-1">Example Request (Bearer Auth)</div>
                    <p class="mb-4 api-key-help-text">Endpoint: https://api.quaro.io/v2/research/search-volume/quaro (language_code: en, location_code: 2840)</p>

                    <v-select v-model="selectedExample" :items="exampleOptions" item-text="text" item-value="value" label="Example Type" dense outlined hide-details class="mb-4 example-select" />

                    <div class="snippet-title">{{ activeSnippetTitle }}</div>
                    <pre class="snippet-box"><code>{{ activeSnippet }}</code></pre>
                    <v-btn small outlined color="primary" @click="copyText(activeSnippet)">{{ activeCopyLabel }}</v-btn>
                </v-card>
            </v-col>
        </v-row>

        <!-- Company-weiter Key, nur für Admins. Solange user_api_key_feature_enabled=false ist das hier der
             einzige Key ueberhaupt und deshalb per Default aufgeklappt; sonst (Personal-Key aktiv) bleibt es
             ein eingeklapptes "Legacy"-Panel, siehe legacyPanel-Default unten. -->
        <v-row v-if="isAdmin" class="mt-1">
            <v-col cols="12">
                <v-expansion-panels v-model="legacyPanel" flat class="legacy-panel" @change="onLegacyPanelToggle">
                    <v-expansion-panel>
                        <v-expansion-panel-header>{{ userApiKeyFeatureEnabled ? "Company API Key (Legacy)" : "Company API Key" }}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <p class="mb-4 api-key-help-text">
                                <template v-if="userApiKeyFeatureEnabled">
                                    This key belongs to the whole company, not to any single user. Only use it for existing integrations &ndash; for anything new, including the MCP server, use your
                                    personal API key above. Regenerating it disconnects <strong>every</strong> integration in the company that still uses it.
                                </template>
                                <template v-else>
                                    This key belongs to the whole company, not to any single user. Regenerating it disconnects <strong>every</strong> integration in the company that still uses it.
                                </template>
                            </p>

                            <v-text-field :value="displayCompanyApiKey" label="Company API key" readonly filled dense hide-details prepend-inner-icon="mdi-key-variant" aria-live="polite">
                                <template v-slot:append>
                                    <v-btn
                                        icon
                                        x-small
                                        @click.stop.prevent="toggleCompanyApiKey"
                                        :disabled="companyLoading || !companyApiKey"
                                        :aria-label="showCompanyApiKey ? 'Hide company API key' : 'Show company API key'"
                                        :title="showCompanyApiKey ? 'Hide company API key' : 'Show company API key'">
                                        <v-icon small>{{ showCompanyApiKey ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                                    </v-btn>

                                    <v-btn
                                        icon
                                        x-small
                                        @click.stop.prevent="copyCompanyApiKey"
                                        :disabled="companyLoading || !companyApiKey"
                                        aria-label="Copy company API key"
                                        title="Copy company API key">
                                        <v-icon small>mdi-content-copy</v-icon>
                                    </v-btn>
                                </template>
                            </v-text-field>

                            <div class="mt-4">
                                <v-btn color="error" rounded @click="showCompanyRegenerateConfirm = true" :disabled="companyLoading">Regenerate company API key</v-btn>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>

        <v-dialog v-model="showRegenerateConfirm" max-width="520">
            <v-card>
                <v-card-title class="headline">Generate new API key?</v-card-title>
                <v-card-text>
                    Your old personal API key will be deleted and will no longer work. Every integration and MCP connection you use in this company loses access until you share the new key.
                    <v-checkbox v-model="confirmRegenerate" class="mt-4" hide-details label="I confirm that I want to regenerate my API key." />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeRegenerateDialog">Cancel</v-btn>
                    <v-btn color="error" rounded @click="regenerateApiKey" :loading="loading" :disabled="!confirmRegenerate">Generate</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showCompanyRegenerateConfirm" max-width="520">
            <v-card>
                <v-card-title class="headline">Generate new company API key?</v-card-title>
                <v-card-text>
                    The old company API key will be deleted and will no longer work. Every integration in the company that uses it loses access immediately.
                    <v-checkbox v-model="confirmCompanyRegenerate" class="mt-4" hide-details label="I confirm that I want to regenerate the company API key." />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeCompanyRegenerateDialog">Cancel</v-btn>
                    <v-btn color="error" rounded @click="regenerateCompanyApiKey" :loading="companyLoading" :disabled="!confirmCompanyRegenerate">Generate</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import { fetchPersonalApiKey, createOrRegeneratePersonalApiKey } from "./helper/personalApiKey"

    export default {
        data() {
            return {
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
                showApiKey: false,
                loading: false,
                showRegenerateConfirm: false,
                confirmRegenerate: false,
                selectedExample: "curl",
                // Legacy company key - bei deaktiviertem Personal-Key (siehe userApiKeyFeatureEnabled) ist
                // das der einzige Key und startet deshalb aufgeklappt (Panel 0), sonst eingeklappt (null).
                legacyPanel: this.$store.state.user_api_key_feature_enabled ? null : 0,
                companyKeyLoaded: false,
                companyApiKey: "",
                showCompanyApiKey: false,
                companyLoading: false,
                showCompanyRegenerateConfirm: false,
                confirmCompanyRegenerate: false
            }
        },
        computed: {
            isAdmin() {
                return !!(this.$store && this.$store.state && this.$store.state.admin)
            },
            userApiKeyFeatureEnabled() {
                return !!(this.$store && this.$store.state && this.$store.state.user_api_key_feature_enabled)
            },
            // Aus dem Store statt lokalem data() - McpSettings.vue liest/schreibt denselben Wert, siehe
            // Kommentar an personal_api_key im Store.
            apiKey() {
                return this.$store.state.personal_api_key || ""
            },
            apiSearchVolumeUrl() {
                return "https://api.quaro.io/v2/research/search-volume/quaro"
            },
            displayApiKey() {
                if (!this.apiKey) return ""
                if (this.showApiKey) return this.apiKey
                return this.maskedApiKey(this.apiKey)
            },
            displayCompanyApiKey() {
                if (!this.companyApiKey) return "************************"
                if (this.showCompanyApiKey) return this.companyApiKey
                return this.maskedApiKey(this.companyApiKey)
            },
            activeSnippetTitle() {
                if (this.selectedExample === "js") return "JavaScript (fetch)"
                if (this.selectedExample === "python") return "Python (requests)"
                return "cURL"
            },
            activeSnippet() {
                if (this.selectedExample === "js") return this.fetchSnippet
                if (this.selectedExample === "python") return this.pythonSnippet
                return this.curlSnippet
            },
            activeCopyLabel() {
                if (this.selectedExample === "js") return "Copy JS"
                if (this.selectedExample === "python") return "Copy Python"
                return "Copy cURL"
            },
            exampleOptions() {
                return [
                    { text: "cURL", value: "curl" },
                    { text: "JavaScript", value: "js" },
                    { text: "Python", value: "python" }
                ]
            },
            curlSnippet() {
                return [
                    'curl -X POST "' + this.apiSearchVolumeUrl + '" \\',
                    '  -H "Authorization: Bearer <YOUR_BEARER_TOKEN>" \\',
                    '  -H "Content-Type: application/json" \\',
                    '  -d \'{"keywords":["ai seo content brief","saas seo strategy"],"language_code":"en","location_code":2840}\''
                ].join("\n")
            },
            fetchSnippet() {
                return [
                    'fetch("' + this.apiSearchVolumeUrl + '", {',
                    '  method: "POST",',
                    "  headers: {",
                    '    Authorization: "Bearer <YOUR_BEARER_TOKEN>",',
                    '    "Content-Type": "application/json"',
                    "  },",
                    "  body: JSON.stringify({",
                    '    keywords: ["ai seo content brief", "saas seo strategy"],',
                    '    language_code: "en",',
                    "    location_code: 2840",
                    "  })",
                    "})",
                    "  .then((res) => res.json())",
                    "  .then((data) => console.log(data));"
                ].join("\n")
            },
            pythonSnippet() {
                return [
                    "import requests",
                    "",
                    'url = "' + this.apiSearchVolumeUrl + '"',
                    "headers = {",
                    '    "Authorization": "Bearer <YOUR_BEARER_TOKEN>",',
                    '    "Content-Type": "application/json"',
                    "}",
                    "payload = {",
                    '    "keywords": ["ai seo content brief", "saas seo strategy"],',
                    '    "language_code": "en",',
                    '    "location_code": 2840',
                    "}",
                    "",
                    "response = requests.post(url, headers=headers, json=payload)",
                    "print(response.json())"
                ].join("\n")
            }
        },
        methods: {
            createInfo(message, color, icon) {
                this.$emit("info", message, color, icon)
            },
            maskedApiKey(value) {
                if (!value) return "************************"
                return "*".repeat(value.length)
            },
            toggleApiKey() {
                this.showApiKey = !this.showApiKey
            },
            toggleCompanyApiKey() {
                if (!this.isAdmin) return
                this.showCompanyApiKey = !this.showCompanyApiKey
            },
            async copyText(value) {
                try {
                    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(value)
                    } else {
                        const helperInput = document.createElement("input")
                        helperInput.value = value
                        document.body.appendChild(helperInput)
                        helperInput.select()
                        document.execCommand("copy")
                        document.body.removeChild(helperInput)
                    }
                    this.createInfo("Copied to clipboard.", "green", "mdi-check-circle-outline")
                } catch (error) {
                    this.createInfo("Failed to copy.", "#fcba03", "mdi-alert-circle-outline")
                }
            },
            async copyApiKey() {
                if (!this.apiKey) {
                    this.createInfo("No API key available.", "#fcba03", "mdi-alert-circle-outline")
                    return
                }
                await this.copyText(this.apiKey)
            },
            async copyCompanyApiKey() {
                if (!this.companyApiKey) {
                    this.createInfo("No API key available.", "#fcba03", "mdi-alert-circle-outline")
                    return
                }
                await this.copyText(this.companyApiKey)
            },
            // Personal-Key nur laden, wenn er noch nie geholt wurde (null) - der Store ist danach die
            // Wahrheit, ein Regenerate im MCP-Tab schreibt ohnehin hinein.
            fetchApiKey() {
                if (this.$store.state.personal_api_key !== null) return
                this.loading = true
                fetchPersonalApiKey(this.$http, this.auth)
                    .then(() => {
                        this.showApiKey = false
                    })
                    .catch((error) => {
                        this.createInfo(error.message, "#fcba03", "mdi-alert-circle-outline")
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            closeRegenerateDialog() {
                this.showRegenerateConfirm = false
                this.confirmRegenerate = false
            },
            generateApiKey() {
                this.requestNewApiKey()
            },
            regenerateApiKey() {
                if (!this.confirmRegenerate) return
                this.requestNewApiKey(() => this.closeRegenerateDialog())
            },
            requestNewApiKey(onSuccess) {
                // Vor dem Request merken: gab es schon einen Key? Erstanlage und Regenerate laufen über
                // denselben Endpunkt (Upsert), aber "the old key is no longer valid" ist bei der Erstanlage
                // irreführend - es gab ja keinen alten Key.
                const hadKey = !!this.apiKey
                this.loading = true
                createOrRegeneratePersonalApiKey(this.$http, this.auth)
                    .then(() => {
                        this.showApiKey = false
                        if (onSuccess) onSuccess()
                        if (hadKey) this.createInfo("New API key generated. The old key is no longer valid.", "green", "mdi-check-circle-outline")
                        else this.createInfo("API key created.", "green", "mdi-check-circle-outline")
                    })
                    .catch((error) => {
                        this.createInfo(error.message, "#fcba03", "mdi-alert-circle-outline")
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            // Legacy company key
            onLegacyPanelToggle(value) {
                if (value === 0 && !this.companyKeyLoaded) {
                    this.companyKeyLoaded = true
                    this.fetchCompanyApiKey()
                }
            },
            fetchCompanyApiKey() {
                if (!this.isAdmin) return
                this.companyLoading = true
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/company/apikey", { company_id: localStorage.company_id }, { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.companyApiKey = (response.body && response.body.api_key) || ""
                        this.showCompanyApiKey = false
                        // Store: mit McpSettings.vue geteilt, siehe Kommentar an company_api_key im Store.
                        this.$store.commit("SET_COMPANY_API_KEY", this.companyApiKey)
                    })
                    .catch((error) => {
                        console.log(error)
                        this.createInfo("Failed to load the company API key.", "#fcba03", "mdi-alert-circle-outline")
                    })
                    .finally(() => {
                        this.companyLoading = false
                    })
            },
            closeCompanyRegenerateDialog() {
                this.showCompanyRegenerateConfirm = false
                this.confirmCompanyRegenerate = false
            },
            regenerateCompanyApiKey() {
                if (!this.isAdmin || !this.confirmCompanyRegenerate) return
                this.companyLoading = true
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/company/apikey/regenerate", { company_id: localStorage.company_id }, { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.companyApiKey = (response.body && response.body.api_key) || ""
                        this.showCompanyApiKey = false
                        this.closeCompanyRegenerateDialog()
                        // Store: mit McpSettings.vue geteilt, damit ein bereits geoeffneter MCP-Tab die neue
                        // Connection-URL sofort zeigt, ohne neu geladen werden zu muessen.
                        this.$store.commit("SET_COMPANY_API_KEY", this.companyApiKey)
                        this.createInfo("New company API key generated. The old key is no longer valid.", "green", "mdi-check-circle-outline")
                    })
                    .catch((error) => {
                        console.log(error)
                        this.createInfo("Failed to generate a new company API key.", "#fcba03", "mdi-alert-circle-outline")
                    })
                    .finally(() => {
                        this.companyLoading = false
                    })
            }
        },
        mounted() {
            // Personal-Key deaktiviert -> keinen unnoetigen /user/apikey-Request ausloesen, stattdessen
            // gleich den Company-Key laden (das Panel startet dann bereits aufgeklappt, siehe legacyPanel).
            if (this.userApiKeyFeatureEnabled) {
                this.fetchApiKey()
                return
            }
            if (this.isAdmin && !this.companyKeyLoaded) {
                this.companyKeyLoaded = true
                this.fetchCompanyApiKey()
            }
        }
    }
</script>

<style scoped>
    .api-key-help-text {
        color: #4c4c4c;
    }

    .api-key-page {
        max-width: 1360px;
    }

    .home-like-header {
        background: linear-gradient(90deg, rgb(245 254 251 / 90%) 0%, rgb(249 245 247 / 90%) 100%);
        border: 1px solid #e9efed;
        border-radius: 12px;
        padding: 14px 16px;
    }

    .api-card {
        border-radius: 12px;
    }

    .docs-card {
        border-radius: 12px;
        min-height: 140px;
        width: 100%;
    }

    .snippet-title {
        font-weight: 700;
        margin-bottom: 8px;
    }

    .example-select {
        max-width: 320px;
    }

    .snippet-box {
        background: #f6f8fb;
        border: 1px solid #e4e8ef;
        border-radius: 10px;
        padding: 12px;
        min-height: 190px;
        font-size: 12px;
        line-height: 1.45;
        white-space: pre-wrap;
        word-break: break-word;
        margin-bottom: 10px;
    }

    .docs-link {
        color: #1565c0;
        text-decoration: none;
        font-weight: 500;
    }

    .docs-link:hover {
        text-decoration: underline;
    }

    .gap-2 {
        gap: 8px;
    }

    .not-admin-hint {
        color: #4c4c4c;
        font-size: 14px;
    }

    .legacy-panel {
        border: 1px solid #e4e8ef;
        border-radius: 10px;
    }
</style>
